/**
 * ChangelogWidget — ป้ายเล็กๆ มุมซ้ายล่าง บอก "วันที่ปรับปรุงล่าสุด".
 * กดแล้วเปิด popup รายการอัปเดต (วันที่ + หัวข้อย่อย). เด้งจุดแดงถ้ามีอัปเดตที่
 * ผู้ใช้ยังไม่เปิดดู (จำใน localStorage). โชว์เฉพาะหน้าแอดมิน (ใส่ไว้ใน AdminLayout).
 */
import { useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { CHANGELOG, LATEST_CHANGE, thaiDateShort, thaiDateFull } from '../lib/changelog';

const SEEN_KEY = 'payroll-edge.changelog.seen';

export function ChangelogWidget() {
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const latest = LATEST_CHANGE;

  // เช็คว่ามีอัปเดตที่ยังไม่ได้ดูไหม (เทียบวันที่ล่าสุดกับที่เคยเปิด)
  useEffect(() => {
    if (!latest) return;
    try {
      setHasNew(localStorage.getItem(SEEN_KEY) !== latest.date);
    } catch { /* ignore */ }
  }, [latest]);

  // ปิดเมื่อคลิกนอก popup
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!latest) return null;

  function toggle() {
    setOpen((v) => {
      const next = !v;
      if (next && latest) {
        try { localStorage.setItem(SEEN_KEY, latest.date); } catch { /* ignore */ }
        setHasNew(false);
      }
      return next;
    });
  }

  return (
    <div ref={rootRef} className="fixed bottom-4 left-4 z-[55] print:hidden">
      {open && (
        <div className="mb-2 w-[min(20rem,calc(100vw-2rem))] max-h-[60vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
          <div className="sticky top-0 flex items-center justify-between gap-2 border-b border-gray-100 bg-white px-4 py-2.5">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              <Sparkles className="h-4 w-4 text-brand-500" /> อัปเดตระบบ
            </div>
            <button onClick={() => setOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="ปิด">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {CHANGELOG.map((entry) => (
              <div key={entry.date} className="px-4 py-3">
                <div className="mb-1.5 text-xs font-semibold text-brand-600">{thaiDateFull(entry.date)}</div>
                <ul className="space-y-1">
                  {entry.items.map((it, i) => (
                    <li key={i} className="flex gap-1.5 text-xs leading-relaxed text-gray-600">
                      <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={toggle}
        className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/95 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-md backdrop-blur hover:bg-white hover:text-brand-600"
        title="ดูรายการอัปเดตระบบ"
      >
        <Sparkles className="h-3.5 w-3.5 text-brand-500" />
        <span>อัปเดต {thaiDateShort(latest.date)}</span>
        {hasNew && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" aria-label="มีอัปเดตใหม่" />}
      </button>
    </div>
  );
}
